import { useEffect, useState } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { LanguageProvider } from '@/contexts/LanguageContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { Toaster, toast } from '@/components/ui/sonner';
import Header from '@/components/Header';
import AuthCallback from '@/components/AuthCallback';
import LandingPage from '@/pages/LandingPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ApplicationForm from '@/pages/ApplicationForm';
import TenantDashboard from '@/pages/TenantDashboard';
import AdminPanel from '@/pages/AdminPanel';
import AdminApartmentsPage from '@/pages/admin/AdminApartmentsPage';
import AdminApartmentNewPage from '@/pages/admin/AdminApartmentNewPage';
import AdminApartmentDetailPage from '@/pages/admin/AdminApartmentDetailPage';
import AdminApartmentEditPage from '@/pages/admin/AdminApartmentEditPage';
import ServicesPage from '@/pages/ServicesPage';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import api from '@/lib/api';
import { loadEnums } from '@/lib/enums';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F3EF]" data-testid="loading-screen">
      <div className="w-8 h-8 border-2 border-[#0B3D2E] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ForcePasswordReset() {
  const { user, setUser } = useAuth();
  const { t } = useLanguage();
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  if (!user || !user.force_password_reset) return null;

  const handleSubmit = async () => {
    if (newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await api.post('/auth/change-password', { new_password: newPassword });
      setUser({ ...user, force_password_reset: false });
      toast.success('Password changed successfully');
    } catch {
      toast.error('Failed to change password');
    }
    setLoading(false);
  };

  return (
    <Dialog open={true}>
      <DialogContent className="sm:max-w-md" data-testid="force-password-reset-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t('changePass.title')}</DialogTitle>
          <DialogDescription>{t('changePass.desc')}</DialogDescription>
        </DialogHeader>
        <Input
          type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
          placeholder={t('changePass.new')} className="h-11" data-testid="new-password-input"
        />
        <DialogFooter>
          <Button onClick={handleSubmit} disabled={loading || !newPassword} className="bg-[#0B3D2E] text-white" data-testid="change-password-btn">
            {loading ? 'Saving...' : t('changePass.btn')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppRouter() {
  const location = useLocation();

  // Fetch the server enum contract once so pages don't have to coordinate
  // their own fallback constants. Non-blocking: pages render with
  // FALLBACK_ENUMS until the fetch resolves.
  useEffect(() => { loadEnums(); }, []);

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const showHeader = !['/login', '/register'].includes(location.pathname);

  return (
    <>
      {showHeader && <Header />}
      <ForcePasswordReset />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/application" element={<ApplicationForm />} />
        <Route path="/dashboard" element={<ProtectedRoute><TenantDashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute><AdminPanel /></AdminRoute>} />
        <Route path="/admin/apartments" element={<AdminRoute><AdminApartmentsPage /></AdminRoute>} />
        <Route path="/admin/apartments/new" element={<AdminRoute><AdminApartmentNewPage /></AdminRoute>} />
        <Route path="/admin/apartments/:aptId/edit" element={<AdminRoute><AdminApartmentEditPage /></AdminRoute>} />
        <Route path="/admin/apartments/:aptId" element={<AdminRoute><AdminApartmentDetailPage /></AdminRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  // AuthProvider wraps LanguageProvider because the language context now
  // reads `user.preferred_language` from auth state and writes back to
  // PATCH /auth/me when the user flips the switcher.
  return (
    <AuthProvider>
      <LanguageProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster position="top-right" />
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
